/**
 * fleetReducer — pure state transitions for the active-trip map
 * ({ [tripId]: NormalizedTrip }). No React, no side effects.
 *
 * See specs/022-live-tracking-stability/data-model.md.
 */
import { normalizeTrip, deriveTripFields } from './normalizeTrip';

// FR-002: REST response REPLACES active-trip state (ghost elimination).
export const applySnapshot = (list) => {
  const next = {};
  (Array.isArray(list) ? list : []).forEach((rest) => {
    const rec = normalizeTrip(rest);
    if (rec) next[rec.tripId] = rec;
  });
  return next;
};

// FR-004 / FR-014 / Q1 / Q2: merge ONLY dynamic fields from a trip-location-update
// onto an existing trip. Unknown trip -> ignore. Older `time` -> ignore. A terminal
// `tripStatus` (e.g. COMPLETED) is merged for display but never removes the trip.
export const mergeLocationUpdate = (state, msg) => {
  const tripId = msg?.tripId;
  if (!tripId) return state;

  const existing = state[tripId];
  if (!existing) return state; // unknown trip (Q2) — appears only via REST refresh

  if (
    typeof msg.time === 'number' &&
    typeof existing._lastTime === 'number' &&
    msg.time < existing._lastTime
  ) {
    return state; // out-of-order (FR-014)
  }

  const lf = msg.liveFlags || {};
  const merged = deriveTripFields({
    ...existing,
    driverId: msg.driverId || existing.driverId || null,
    // dynamic fields
    latitude: msg.latitude != null ? msg.latitude : existing.latitude,
    longitude: msg.longitude != null ? msg.longitude : existing.longitude,
    heading: msg.heading != null ? msg.heading : existing.heading,
    speed: msg.speed != null ? msg.speed : existing.speed,
    tripStatus: msg.tripStatus || existing.tripStatus,
    onboardCount: msg.onboardCount != null ? msg.onboardCount : existing.onboardCount,
    time: msg.time != null ? msg.time : existing.time,
    vehicleNearOffice: msg.vehicleNearOffice === true,
    isOverSpeeding: msg.isOverSpeeding === true,
    safetyCheckRequired: msg.safetyCheckRequired === true,
    geoFenceViolation: lf.geoFenceViolation === true,
    GPS_SIGNAL_LOST: msg.GPS_SIGNAL_LOST === true,
    isServerGenerated: msg.isServerGenerated === true,
    scheduledStopsETA: msg.scheduledStopsETA || existing.scheduledStopsETA,
    finalDestinationETA:
      msg.finalDestinationETA != null ? msg.finalDestinationETA : existing.finalDestinationETA,
    passengerETA: msg.passengerETA || existing.passengerETA,
    metadata: {
      ...existing.metadata,
      tripFlags: {
        ...existing.metadata.tripFlags, // preserve static flags
        isSOS: lf.isSOS === true,
        hasStoppedOver5Mins: lf.hasStoppedOver5Mins === true,
        isMaxTravelTimeViolation: lf.isMaxTravelTimeViolation === true,
      },
      delayFlags: lf.delayFlags || existing.metadata.delayFlags || {},
    },
    // bookkeeping
    _lastTime: msg.time != null ? msg.time : existing._lastTime,
    _lastUpdatedAt: Date.now(),
    _stale: false, // a fresh update revives a greyed trip (FR-017)
  });

  return { ...state, [tripId]: merged };
};

// FR-016a / Q3 / Q4: grey (not remove) active trips that stop reporting past the
// staleness window. They remain in state so they stay counted/listed.
export const markStale = (state, now, staleMs) => {
  let changed = false;
  const next = {};
  for (const [tripId, rec] of Object.entries(state)) {
    if (!rec._stale && now - (rec._lastUpdatedAt || 0) > staleMs) {
      next[tripId] = { ...rec, _stale: true };
      changed = true;
    } else {
      next[tripId] = rec;
    }
  }
  return changed ? next : state;
};
